import { create } from 'zustand';
import api from '../services/api';

// ─── 型別定義 ───────────────────────────────────────────

export interface CatalogTab {
  key: string;
  label: string;
  categories: string[];
}

export interface CircuitDevice {
  hitachi?: string;
  xr60?: string;
  iot627?: string;
  [role: string]: string | undefined;
}

export interface CatalogCircuit {
  index: number;
  label: string;
  devices: CircuitDevice;
}

export interface CatalogWritableParam {
  param: string;
  min: number;
  max: number;
  unit: string;
}

export interface CatalogDevice {
  device_key: string;
  name: string;
  type: string;
  category: string;
  slave_id: number;
  storage_id?: number;
  machine_id?: number;
  tag_prefix?: string;
  writable_params?: CatalogWritableParam[];
  has_defrost_trigger?: boolean;
}

// ─── Store 狀態 ───────────────────────────────────────────

interface DeviceCatalogState {
  /** 是否已載入 */
  loaded: boolean;
  /** 租戶 ID */
  tenant: string;
  /** 站點名稱 */
  site: string;
  /** 監控頁面 Tab 分組 */
  tabs: CatalogTab[];
  /** 冷凍迴路定義 */
  circuits: CatalogCircuit[];
  /** 獨立設備（不屬於迴路） */
  auxiliaryDevices: string[];
  /** 設備清單 */
  devices: CatalogDevice[];
  /** 歷史數據 tag 標籤 */
  tagLabels: Record<string, string>;

  // ─── Actions ───
  fetchCatalog: () => Promise<void>;

  // ─── 計算方法 ───
  /** 依 Tab key 取得該 Tab 下的設備 */
  getDevicesByTab: (tabKey: string) => CatalogDevice[];
  /** 依 Tab key 取得該 Tab 下的設備 key 列表 */
  getTabDeviceKeys: (tabKey: string) => string[];
  /** 取得所有 Tab 對應的 device keys mapping（取代 MONITORING_TAB_DEVICES） */
  getTabDeviceKeysMap: () => Record<string, string[]>;
  /** 依 category 取得設備 */
  getDevicesByCategory: (category: string) => CatalogDevice[];
  /** 依 device_key 取得單一設備定義 */
  getDevice: (deviceKey: string) => CatalogDevice | undefined;
  /** 取得 tag 中文標籤（fallback 回傳原始 tagName） */
  getTagLabel: (tagName: string) => string;
}

const useDeviceCatalogStore = create<DeviceCatalogState>((set, get) => ({
  loaded: false,
  tenant: '',
  site: '',
  tabs: [],
  circuits: [],
  auxiliaryDevices: [],
  devices: [],
  tagLabels: {},

  fetchCatalog: async () => {
    try {
      const res = await api.get('/devices/config');
      if (res.data?.success) {
        const d = res.data.data;
        set({
          loaded: true,
          tenant: d.tenant || '',
          site: d.site || '',
          tabs: d.tabs || [],
          circuits: d.circuits || [],
          auxiliaryDevices: d.auxiliary_devices || [],
          devices: d.devices || [],
          tagLabels: d.tag_labels || {},
        });
      }
    } catch {
      // API 失敗時保持空狀態，前端 fallback 到靜態常數
    }
  },

  getDevicesByTab: (tabKey) => {
    const { tabs, devices } = get();
    const tab = tabs.find(t => t.key === tabKey);
    if (!tab) return [];
    return devices.filter(d => tab.categories.includes(d.category));
  },

  getTabDeviceKeys: (tabKey) => {
    return get().getDevicesByTab(tabKey).map(d => d.device_key);
  },

  getTabDeviceKeysMap: () => {
    const { tabs } = get();
    const result: Record<string, string[]> = {};
    for (const tab of tabs) {
      result[tab.key] = get().getTabDeviceKeys(tab.key);
    }
    return result;
  },

  getDevicesByCategory: (category) => {
    return get().devices.filter(d => d.category === category);
  },

  getDevice: (deviceKey) => {
    return get().devices.find(d => d.device_key === deviceKey);
  },

  getTagLabel: (tagName) => {
    return get().tagLabels[tagName] || tagName;
  },
}));

export default useDeviceCatalogStore;
